import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, Send, X, Users, Wifi, WifiOff, 
  ChevronDown, Loader2 
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export function ChatWindow({ 
  orderId, 
  isOpen, 
  onClose,
  minimized = false,
  onToggleMinimize 
}) {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket connection effect
  useEffect(() => {
    if (!orderId || !token || !isOpen) {
      return;
    }
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/chat/${orderId}?token=${token}`;
    
    let ws = null;
    let reconnectTimer = null;
    
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          setConnected(true);
          setLoading(false);
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'history') {
              setMessages(data.messages || []);
              setConnectedUsers(data.connected_users || []);
            }
            
            if (data.type === 'new_message') {
              setMessages(prev => [...prev, data.message]);
            }
            
            if (data.type === 'user_joined') {
              setConnectedUsers(prev => {
                if (!prev.find(u => u.id === data.user.id)) {
                  return [...prev, data.user];
                }
                return prev;
              });
            }
            
            if (data.type === 'user_left') {
              setConnectedUsers(prev => prev.filter(u => u.id !== data.user.id));
            }
            
            if (data.type === 'typing') {
              const userId = user?.id;
              if (data.is_typing && data.user.id !== userId) {
                setTypingUsers(prev => {
                  if (!prev.find(u => u.id === data.user.id)) {
                    return [...prev, data.user];
                  }
                  return prev;
                });
              } else {
                setTypingUsers(prev => prev.filter(u => u.id !== data.user.id));
              }
            }
          } catch (e) {
            console.error('Chat message parse error:', e);
          }
        };
        
        ws.onclose = () => {
          setConnected(false);
          // Schedule reconnect
          reconnectTimer = setTimeout(connect, 5000);
        };
        
        ws.onerror = () => {
          setConnected(false);
          setLoading(false);
        };
      } catch (e) {
        console.error('WebSocket connection error:', e);
        setLoading(false);
      }
    };
    
    connect();
    
    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (ws) {
        ws.close();
      }
    };
  }, [orderId, token, isOpen, user?.id]);

  const sendMessage = () => {
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    
    wsRef.current.send(JSON.stringify({
      type: 'message',
      message: newMessage.trim()
    }));
    
    setNewMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: true
      }));
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'typing',
            is_typing: false
          }));
        }
      }, 2000);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'customer': return 'bg-blue-500';
      case 'driver': return 'bg-green-500';
      case 'restaurant_admin': return 'bg-orange-500';
      default: return 'bg-slate-500';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'customer': return 'Customer';
      case 'driver': return 'Driver';
      case 'restaurant_admin': return 'Restaurant';
      default: return role;
    }
  };

  if (!isOpen) return null;

  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50"
        onClick={onToggleMinimize}
      >
        <Button className="rounded-full w-14 h-14 shadow-lg bg-orange-500 hover:bg-orange-600">
          <MessageCircle className="w-6 h-6" />
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
              {messages.length}
            </span>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <span className="font-semibold">Order Chat</span>
          {connected ? (
            <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">
              <Wifi className="w-3 h-3" />
              Live
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs bg-red-500/50 px-2 py-0.5 rounded-full">
              <WifiOff className="w-3 h-3" />
              Offline
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-white/20 h-8 w-8 p-0"
            onClick={onToggleMinimize}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-white/20 h-8 w-8 p-0"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Connected Users */}
      {connectedUsers.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 border-b flex items-center gap-2 text-sm">
          <Users className="w-4 h-4 text-slate-400" />
          <div className="flex items-center gap-1 overflow-x-auto">
            {connectedUsers.map((u) => (
              <span 
                key={u.id} 
                className={`px-2 py-0.5 rounded-full text-xs text-white ${getRoleColor(u.role)}`}
              >
                {u.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-80 min-h-[200px] bg-slate-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 py-8">
            <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === user?.id;
            return (
              <div 
                key={msg.id} 
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${isMe ? 'order-2' : ''}`}>
                  {!isMe && (
                    <div className="flex items-center gap-1 mb-1">
                      <span className={`w-2 h-2 rounded-full ${getRoleColor(msg.sender_role)}`} />
                      <span className="text-xs text-slate-500">
                        {msg.sender_name} • {getRoleLabel(msg.sender_role)}
                      </span>
                    </div>
                  )}
                  <div 
                    className={`rounded-2xl px-4 py-2 ${
                      isMe 
                        ? 'bg-orange-500 text-white rounded-br-sm' 
                        : 'bg-white text-slate-900 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                  <span className={`text-xs text-slate-400 mt-1 block ${isMe ? 'text-right' : ''}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })
        )}
        
        {/* Typing Indicator */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>{typingUsers.map(u => u.name).join(', ')} typing...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1"
            disabled={!connected}
            data-testid="chat-input"
          />
          <Button 
            onClick={sendMessage}
            disabled={!connected || !newMessage.trim()}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="send-message-button"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ChatWindow;
